import { useState, useMemo, useEffect } from 'react';
import { Box, Tooltip, Collapse, Typography, Divider, Slider, TextField } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ExpandMore } from './ExpandMore';
import { formatDate, calculateEndDate, mapToProductName } from './utils';
import { currencyFormatter, financial, percentageFormatter } from '@lendica/utils';
import { useContainerWidth } from '@lendica/hooks';

const MIN_PERIOD = 1;

const PAYMENT_SCHEMAS = {
    WEEKLY: { label: 'Weekly', divisor: 7 },
    MONTHLY: { label: 'Monthly', divisor: 30 },
    BULLET: { label: 'Bullet', divisor: null }
};

const formatPaymentSchema = (paymentSchema) => {
    return PAYMENT_SCHEMAS[paymentSchema]?.label || paymentSchema || '';
};

const calculateNumberOfPayments = (period, paymentSchema) => {
    const schema = PAYMENT_SCHEMAS[paymentSchema];
    if (!schema) return null;

    return schema.divisor ? Math.max(Math.floor(period / schema.divisor), 1) : 1;
};

export const ApprovalAccordion = ({ approval }) => {
    const { isNarrow } = useContainerWidth();
    const [expanded, setExpanded] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState(MIN_PERIOD);

    if (!approval || !approval.rateCurve) {
        return null;
    }

    const startDate = new Date(approval.contract_start_date);
    const endDate = calculateEndDate(startDate, approval.term_length_in_days);

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };

    const maxPeriod = useMemo(() => {
        return Math.max(...approval.rateCurve.map(r => r.day));
    }, [approval.rateCurve]);

    useEffect(() => {
        if (maxPeriod > 0) {
            setSelectedPeriod(maxPeriod);
        }
    }, [maxPeriod]);

    const handlePeriodChange = (event, newValue) => {
        setSelectedPeriod(newValue);
    };

    const handlePeriodInputChange = (event) => {
        const value = parseInt(event.target.value, 10);
        if (!isNaN(value) && value >= MIN_PERIOD && value <= maxPeriod) {
            setSelectedPeriod(value);
        }
    };

    const calculateTotalFees = () => {
        const getTieredDailyRate = (days) => {
            if (!approval.rateCurve || approval.rateCurve.length === 0) {
                return 0;
            }

            const sortedRates = [...approval.rateCurve].sort((a, b) => a.day - b.day);

            let applicableRate = sortedRates[0];

            for (const rate of sortedRates) {
                if (days <= rate.day) {
                    applicableRate = rate;
                    break;
                }
                applicableRate = rate;
            }

            return applicableRate.daily_rate || 0;
        };

        const dailyRateForPeriod = getTieredDailyRate(selectedPeriod);
        const processingFee = (approval.origination_fee_percentage || 0) * 100;

        // Calculate total fees: (daily_rate * selected_days) + processing_fee
        // Convert daily_rate to percentage (multiply by 100) for display
        return (dailyRateForPeriod * selectedPeriod * 100) + processingFee;
    };

    const totalFeesPercentage = useMemo(() => {
        return calculateTotalFees();
    }, [selectedPeriod, approval.origination_fee_percentage, approval.rateCurve]);

    const numberOfPayments = useMemo(() => {
        return calculateNumberOfPayments(selectedPeriod, approval.payment_schema);
    }, [selectedPeriod, approval.payment_schema]);

    return (
        <Typography variant="caption" sx={{ fontWeight: 500 }}>
            <Box
                sx={{
                    height: 56,
                    alignItems: 'center',
                    flexDirection: 'row',
                    display: 'flex',
                    padding: isNarrow ? '0 8px' : '0 24px',
                    fontSize: '12px',
                    fontWeight: 400,
                    backgroundColor: '#F4F4F6',
                    borderRadius: '4px',
                    '&:hover': {
                        cursor: 'pointer',
                        backgroundColor: 'rgba(140, 140, 161, 0.09)',
                    },
                }}
                onClick={handleExpandClick}
            >
                <Box sx={{ fontSize: '13px', fontWeight: 500 }}>
                    My {mapToProductName(approval.product_name)} approval
                </Box>
                <ExpandMore expand={expanded}>
                    <ArrowDropDownIcon sx={{ fontSize: 18, color: '#4A4A68' }} />
                </ExpandMore>
            </Box>
            <Collapse
                in={expanded}
                timeout="auto"
                unmountOnExit
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    color: '#4A4A68',
                    padding: isNarrow ? '0 8px 12px' : '0 24px 20px',
                    backgroundColor: '#F4F4F6',
                }}
            >
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ height: 24, fontWeight: 300 }}
                >
                    <Box>ID. {approval.id.slice(0, 8)}</Box>
                    <Box>
                        {formatDate(startDate)} - {formatDate(endDate)}
                    </Box>
                </Box>
                <Divider
                    sx={{ backgroundColor: '#8C8CA1', margin: '4px 0', border: 0, height: '0.5px' }}
                />
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ height: 24 }}
                >
                    <Box>Qualified amount</Box>
                    <Box>{currencyFormatter(financial(approval.qualified_amount))}</Box>
                </Box>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ height: 24 }}
                >
                    <Box display="flex" alignItems="center">
                        Advance rate
                        <Tooltip
                            arrow
                            placement="top"
                            PopperProps={{ style: { width: 200 } }}
                            title={
                                <>
                                    <i>Advance Rate</i> - The percentage you will receive from
                                    Lendica for every deal.
                                </>
                            }
                        >
                            <ErrorOutlineIcon sx={{ fontSize: 13, paddingLeft: '2px' }} />
                        </Tooltip>
                    </Box>
                    <Box>{percentageFormatter(approval.advance_rate)}</Box>
                </Box>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ height: 24 }}
                >
                    <Box display="flex" alignItems="center">
                        Processing fee
                        <Tooltip
                            arrow
                            placement="top"
                            PopperProps={{ style: { width: 200 } }}
                            title={
                                <>
                                    <i>Processing Fee</i> - A one-time fee charged for every deal
                                    based on the deal amount.
                                </>
                            }
                        >
                            <ErrorOutlineIcon sx={{ fontSize: 13, paddingLeft: '2px' }} />
                        </Tooltip>
                    </Box>
                    <Box>{percentageFormatter(approval.origination_fee_percentage)}</Box>
                </Box>
                {approval.payment_schema && (
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ height: 24 }}
                    >
                        <Box display="flex" alignItems="center">
                            Payment frequency
                        </Box>
                        <Box>{formatPaymentSchema(approval.payment_schema)}</Box>
                    </Box>
                )}

                <Box display="flex" justifyContent="space-between" sx={{ marginTop: '24px' }}>
                    <Box display="flex" alignItems="center">
                        Total Financing fees
                        <Tooltip
                            arrow
                            placement="top"
                            PopperProps={{ style: { width: 200 } }}
                            title={
                                <>
                                    <i>Total Financing Fee</i> - Total rate charged on your payback amount including processing fee. See how it works for details.
                                </>
                            }
                        >
                            <ErrorOutlineIcon sx={{ fontSize: 13, paddingLeft: '2px' }} />
                        </Tooltip>
                    </Box>
                    {approval.payment_schema && (
                        <Box display="flex" alignItems="center">
                            # of Payments
                            <Tooltip
                                arrow
                                placement="top"
                                PopperProps={{ style: { width: 200 } }}
                                title="Total number of payments over the selected Period, based on the Payment frequency."
                            >
                                <ErrorOutlineIcon sx={{ fontSize: 13, paddingLeft: '2px' }} />
                            </Tooltip>
                        </Box>
                    )}
                    <Box>Period</Box>
                    <Box display="flex" alignItems="center">
                        Total Fees
                        <Tooltip
                            arrow
                            placement="top"
                            PopperProps={{ style: { width: 200 } }}
                            title="Total fee for the selected period including the processing fee"
                        >
                            <ErrorOutlineIcon sx={{ fontSize: 13, paddingLeft: '2px' }} />
                        </Tooltip>
                    </Box>
                </Box>
                <Divider
                    sx={{ backgroundColor: '#8C8CA1', margin: '4px 0', border: 0, height: '0.5px' }}
                />
                {approval.product_name === 'paylater' && approval.rateCurve.map(r => (
                    <Box key={r.id} display="flex" justifyContent="space-between">
                        <Box sx={{ width: '130px' }}></Box>
                        {approval.payment_schema && (
                            <Box sx={{ textAlign: 'center', width: '110px' }}>{calculateNumberOfPayments(r.day, approval.payment_schema)}</Box>
                        )}
                        <Box sx={{ textAlign: 'left', width: '50px' }}>{r.day} days</Box>
                        <Box sx={{ textAlign: 'right', width: '70px' }}>
                            {+(r.daily_rate * r.day * 100).toFixed(2)}%
                        </Box>
                    </Box>
                ))}

                {approval.product_name === 'drawdown' && (
                    <Box display="flex" justifyContent="space-between">
                        <Box sx={{ width: '130px' }}></Box>
                        {approval.payment_schema && (
                            <Box sx={{ textAlign: 'center', width: '110px' }}>{calculateNumberOfPayments(maxPeriod, approval.payment_schema)}</Box>
                        )}
                        <Box sx={{ textAlign: 'left', width: '50px' }}>{maxPeriod} days</Box>
                        <Box sx={{ textAlign: 'right', width: '70px' }}>
                            {+(approval.rateCurve.find(rate => rate.day === maxPeriod).daily_rate * maxPeriod * 100).toFixed(2)}%
                        </Box>
                    </Box>
                )}

                {approval.product_name === 'fundnow' && (
                    <Box display="flex" justifyContent="space-between">
                        <Box sx={{ width: '130px' }}></Box>
                        {approval.payment_schema && (
                            <Box sx={{ textAlign: 'center', width: '80px' }}>
                                {numberOfPayments}
                            </Box>
                        )}
                        <Box display="flex" alignItems="center" flexDirection="column" sx={{ textAlign: 'center', width: '70px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <TextField
                                    size="small"
                                    type="number"
                                    value={selectedPeriod}
                                    onChange={handlePeriodInputChange}
                                    inputProps={{
                                        min: MIN_PERIOD,
                                        max: maxPeriod,
                                        style: { textAlign: 'center' }
                                    }}
                                    sx={{
                                        width: '42px',
                                        paddingRight: '4px',
                                        '& .MuiInputBase-input': {
                                            fontSize: '12px',
                                            padding: '4px'
                                        }
                                    }}
                                />
                                days
                            </Box>
                            <Slider
                                value={selectedPeriod}
                                onChange={handlePeriodChange}
                                min={MIN_PERIOD}
                                max={maxPeriod}
                                step={1}
                                valueLabelDisplay="auto"
                                color="primary"
                                size="small"
                                sx={{
                                    width: '75%',
                                    height: 12,
                                    mt: 1,
                                    padding: '0 !important',
                                    '& .MuiSlider-thumb': {
                                        width: 12,
                                        height: 12,
                                    },
                                    '& .MuiSlider-track': {
                                        height: 2,
                                    },
                                    '& .MuiSlider-rail': {
                                        height: 2,
                                    },
                                    '& .MuiSlider-valueLabel': {
                                        fontSize: '10px',
                                        padding: '2px 6px',
                                    }
                                }}
                            />
                        </Box>

                        <Box sx={{ textAlign: 'right', width: '65px' }}>
                            {totalFeesPercentage.toFixed(2)}%
                        </Box>
                    </Box>
                )}
                <Divider
                    sx={{
                        backgroundColor: '#8C8CA1',
                        marginTop: '4px',
                        border: 0,
                        height: '0.5px',
                    }}
                />
                <Box
                    variant="caption"
                    display="flex"
                    justifyContent="space-between"
                    sx={{ marginTop: '24px', fontWeight: 700, color: '#0E0E2C' }}
                >
                    <Box>Balance</Box>
                    <Box>{currencyFormatter(financial(approval.funds_in_use))}</Box>
                </Box>
                <Box
                    variant="caption"
                    display="flex"
                    justifyContent="space-between"
                    sx={{ fontWeight: 700, color: '#0E0E2C' }}
                >
                    <Box>Credit available</Box>
                    <Box>{currencyFormatter(financial(approval.funds_available))}</Box>
                </Box>
            </Collapse>
        </Typography>
    );
};
